import { useState } from "react";
import { AlertTriangle, Check, Copy } from "lucide-react";

import type { CreateApiKeyResponse } from "@/api";
import { useTranslation } from "@/i18n";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDateID } from "@/lib/format";

interface ApiKeyShowOnceDialogProps {
  keyData: CreateApiKeyResponse | null;
  onClose: () => void;
}

/**
 * Post-create dialog. Shows the full secret_key one time and lets
 * the user copy it. Backdrop-click / Escape are suppressed
 * (``modal=true``) so the secret can't disappear by accident — the
 * user must click "Sudah disimpan, tutup" to dismiss.
 */
export function ApiKeyShowOnceDialog({
  keyData,
  onClose,
}: ApiKeyShowOnceDialogProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  if (!keyData) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(keyData.secret_key);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — user can still select manually.
    }
  };

  const handleClose = () => {
    setCopied(false);
    setAcknowledged(false);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-lg" modal>
      <DialogHeader>
        <DialogTitle>
          <span className="inline-flex items-center gap-2">
            <Check size={18} className="text-success-600" />
            {t("apiKeys.showOnceDialog.title")}
          </span>
        </DialogTitle>
        <DialogDescription>
          {t("apiKeys.showOnceDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="rounded-md border border-ocean-100 bg-ocean-50/40 px-4 py-3">
          <div className="label-eyebrow mb-2">{keyData.name}</div>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 break-all rounded border border-ocean-100 bg-white px-3 py-2 font-mono text-sm text-ocean-900">
              {keyData.secret_key}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="shrink-0"
            >
              {copied ? (
                <>
                  <Check size={14} className="text-success-600" />
                  {t("apiKeys.showOnceDialog.copied")}
                </>
              ) : (
                <>
                  <Copy size={14} />
                  {t("apiKeys.showOnceDialog.copy")}
                </>
              )}
            </Button>
          </div>
        </div>

        <Alert variant="warning">
          <div className="flex items-start gap-2 text-xs">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <strong>{t("apiKeys.showOnceDialog.warningLabel")}</strong>{" "}
              {t("apiKeys.showOnceDialog.warningBody")}
            </div>
          </div>
        </Alert>

        <div className="grid grid-cols-1 gap-1 rounded-md bg-neutral-50 px-3 py-2 text-xs text-neutral-600 tablet:grid-cols-2">
          <div>
            <span className="font-medium text-neutral-800">
              {t("apiKeys.showOnceDialog.rateLimitLabel")}
            </span>{" "}
            {t("apiKeys.showOnceDialog.rateLimitValue", {
              count: keyData.rate_limit_per_minute,
            })}
          </div>
          {keyData.expires_at && (
            <div>
              <span className="font-medium text-neutral-800">
                {t("apiKeys.showOnceDialog.expiresLabel")}
              </span>{" "}
              {formatDateID(keyData.expires_at)}
            </div>
          )}
        </div>

        <label className="flex items-start gap-2 text-xs text-neutral-700">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-ocean-200 text-ocean-600 focus:ring-ocean-400"
          />
          <span>{t("apiKeys.showOnceDialog.acknowledge")}</span>
        </label>
      </DialogBody>

      <DialogFooter>
        <Button
          type="button"
          onClick={handleClose}
          disabled={!acknowledged}
        >
          {t("apiKeys.showOnceDialog.close")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
